/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';

@Component({
  selector: 'app-accessibility-page',
  imports: [CommonModule],
  template: `<div class="ozg-prose prose prose-h1:text-4xl prose-h2:text-2xl">
    <h1>Erklärung zur Barrierefreiheit</h1>
    <p>
      Das Bayerisches Staatsministerium für Digitales ist bemüht, seine Anwendung im Einklang mit der Bayerischen
      Digital-Verordnung (BayDiV) barrierefrei zugänglich zu machen.
    </p>
    <p>
      Diese Erklärung zur Barrierefreiheit gilt für Unterseiten und Subdomains von
      <a href="https://by.ozg-cloud.de">https://by.ozg-cloud.de</a>
    </p>
    <h2>Stand der Vereinbarkeit mit den Anforderungen</h2>
    <p>Diese Anwendung ist wegen der folgenden Unvereinbarkeiten und Ausnahmen teilweise mit §9 BayDiV vereinbar.</p>
    <p>
      Die Anwendung befindet sich derzeit noch in der Entwicklung. An bestehenden und bekannten Barrieren wird gearbeitet, um eine
      Barrierefreiheit gemäß § 3 und § 4 BITV 2.0 gewährleisten zu können.
    </p>
    <h2>Allgemeiner Bereich</h2>
    <h3>Nicht barrierefreie Inhalte</h3>
    <p>Die nachstehend aufgeführten Inhalte sind unvereinbar mit § 3 und § 4 BITV 2.0 und somit nicht barrierefrei:</p>
    <ul>
      <li>Erläuterungen zu den Inhalten und der Navigation dieser Anwendung in leichter Sprache sind nicht vorhanden.</li>
      <li>
        Responsive Darstellung ist nicht vorhanden, weshalb Inhalte nur bedingt verlustfrei vergrößerbar sind oder umbrechen.
      </li>
      <li>Gliedernde Überschriften und Bereiche sind nicht umfassend vorhanden.</li>
      <li>Benutzerdefinierte Einstellungen (Farbauswahl und Schriftgröße) werden nicht umfänglich angewendet.</li>
      <li>Die Einstellungsmöglichkeiten im oberen Navigationsmenü können nicht per Tastatursteuerung erreicht werden.</li>
      <li>Die Vorschläge der Vorgangssuche sind nicht per Screenreader wahrnehmbar.</li>
      <li>Vereinzelt treten Texte mit zu geringem Kontrast zum Hintergrund auf.</li>
      <li>Vereinzelt ist der Tastaturfokus nur schwer wahrnehmbar. Bei Datumspickern ist keine Fokushervorhebung vorhanden.</li>
      <li>
        Es fehlen Statusmeldungen, die Screenreader-Nutzende über Änderungen der Suchergebnisse oder über Änderungen von Anträgen
        informieren.
      </li>
      <li>
        Der als PDF zur Verfügung gestellte Benutzerleitfaden, sowie die als PDF abgespeicherten Nachrichten der Anwendung
        entsprechen nicht dem PDF/UA-Standard.
      </li>
    </ul>
    <h2>Administrationsbereich</h2>
    <h3>Nicht barrierefreie Inhalte</h3>
    <p>Die nachstehend aufgeführten Inhalte sind unvereinbar mit § 3 und § 4 BITV 2.0 und somit nicht barrierefrei:</p>
    <ul>
      <li>Erläuterungen zu den Inhalten und der Navigation dieser Anwendung in leichter Sprache sind nicht vorhanden.</li>
      <li>
        Responsive Darstellung ist nicht vorhanden, weshalb Inhalte nur bedingt verlustfrei vergrößerbar sind oder umbrechen.
      </li>
      <li>Gliedernde Überschriften und Bereiche sind nicht umfassend vorhanden.</li>
      <li>Der Login ist nicht per Tastatursteuerung möglich.</li>
      <li>Der Tastaturfokus ist nur schwer wahrnehmbar.</li>
    </ul>
    <h2>Erstellung dieser Erklärung zur Barrierefreiheit</h2>
    <p>Diese Erklärung wurde am 15.07.2024 erstellt.</p>
    <p>
      Die Aussagen bezüglich der Vereinbarkeit mit den Barrierefreiheitsanforderungen in dieser Erklärung beruhen auf einer
      Selbstbewertung.
    </p>
    <p>Die Erklärung wurde zuletzt am 15.07.2024 aktualisiert.</p>

    <h2>Feedback und Kontaktangaben</h2>
    <p>
      Etwaige Mängel in Bezug auf die Einhaltung der Barrierefreiheitsanforderungen können Sie uns mitteilen unter
      <a href="mailto:bayernportal@stmd.bayern.de">bayernportal&#64;stmd.bayern.de</a>.
    </p>
    <p>
      Zuständig für die barrierefreie Zugänglichkeit und die Bearbeitung der im Rahmen des Feedback-Mechanismus eingehenden
      Mitteilungen ist:
    </p>
    <p>
      Bayerisches Staatsministerium für Digitales <br />
      Oskar-von-Miller-Ring 35 <br />
      80333 München <br />
      E-Mail: <a href="mailto:bayernportal@stmd.bayern.de">bayernportal&#64;stmd.bayern.de</a>
    </p>
    <h2>Durchsetzungsverfahren</h2>
    <p>
      Im Rahmen eines Durchsetzungsverfahrens haben Sie die Möglichkeit,
      <a
        href="https://formularserver.bayern.de/intelliform/forms/rzsued/ldbv/ldbv/anforderungen_barrierefreiheit/index"
        title="Sie verlassen die Seite"
        target="_blank"
        >bei der Durchsetzungsstelle online einen Antrag auf Prüfung der Einhaltung der Anforderungen an die Barrierefreiheit zu
        stellen</a
      >.
    </p>
    <h2>Kontaktdaten der Durchsetzungsstelle</h2>
    <p>
      Landesamt für Digitalisierung, Breitband und Vermessung <br />
      IT-Dienstleistungszentrum des Freistaats Bayern <br />
      Durchsetzungs- und Überwachungsstelle für barrierefreie Informationstechnik <br />
      St.-Martin-Straße 47 <br />
      81541 München
    </p>
    <p>
      E-Mail: <a href="mailto:bitv@bayern.de">bbit&#64;landtag.ltsh.de</a> <br />
      Internet:
      <a href="https://www.ldbv.bayern.de/digitalisierung/bitv.html" title="Sie verlassen die Seite" target="_blank"
        >www.ldbv.bayern.de/digitalisierung/bitv.html</a
      >
    </p>
  </div>`,
})
export class AccessibilityPageComponent {}
